import Fitting from '@/models/Fitting';
import LibertyLensFormSegment from './LibertyLensFormSegment';
import FormPrototype from '@/models/FormPrototype';

/**
 * @typedef {"SYMMETRICAL"|"ASYMMETRICAL"|"QUADRANT"} Geometry
 */

/**
 * The segment configuration of a Liberty lens order form.
 */
export default class LibertyLensForm
{
    // ------------------------------------------------------------ MEMBERS

    /**
     * The segments of the design.
     * @type {LibertyLensFormSegment[]}
     */
    segments = [];

    /**
     * The total diameter of the lens.
     * @type {?number}
     */
    diameter = null;

    /**
     * The peripheral geometry of the lens.
     * @type {?Geometry}
     */
    geometry = null;


    // ------------------------------------------------------------ METHODS

    /**
     * Count the number of meridians.
     *
     * @returns {number}
     */
    countMeridians()
    {
        const meridians = {};
        for(let i = 0; i < this.segments; i++)
        {
            const seg = this.segments[i];
            meridians[seg.meridian] = true;
        }

        return Object.keys(meridians).length;
    }

    /**
     * Count the number of segments by zone, on the first meridian.
     *
     * @returns {Record<string, number>}
     */
    countSegmentsByZone()
    {
        const out = {};
        for(let i = 0; i < this.segments.length; i++)
        {
            const seg = this.segments[i];
            if(seg.meridian !== 'FLAT') // todo: rename meridian to "A"
                continue;

            if(!Object.hasOwn(out, seg.zone))
            {
                out[seg.zone] = 0;
            }

            out[seg.zone]++;
        }

        return out;
    }

    /**
     * Apply the values to a prototype.
     *
     * @param {FormPrototype} proto
     * @returns {void}
     */
    apply(proto)
    {
        if(this.segments.length === 0)
            return;

        const values = {};

        // Set the total diameter & geometry
        values['LENS_DTOT'] = this.diameter;
        values['LENS_PERIPHERAL_ZONE'] = this.geometry;

        // Set the number of segments in each zone
        const segmentsByZone = this.countSegmentsByZone();
        for(const zone in segmentsByZone)
        {
            const nbSegments = segmentsByZone[zone];
            values[`LENS_${zone}_SEGMENT_COUNT`] = nbSegments;
        }

        // Set the parameters of each segment
        let cZone = null;
        let k = 0;
        for(const seg of this.segments)
        {
            if(seg.zone !== cZone)
            {
                cZone = cZone;
                k = 0;
            }
            k++;

            const typCode = `LENS_${seg.meridian}_${seg.zone}_TYPE_${k}`;
            const diaCode = `LENS_${seg.meridian}_${seg.zone}_DIAMETER_${k}`;
            const radCode = `LENS_${seg.meridian}_${seg.zone}_RADIUS_${k}`;
            const eccCode = `LENS_${seg.meridian}_${seg.zone}_ECCENTRICITY_${k}`;
            const sagCode = `LENS_${seg.meridian}_${seg.zone}_SAGITTAL_${k}`;

            // Set curve type & diameter
            values[typCode] = seg.type;
            values[diaCode] = seg.diameter;

            switch(seg.type)
            {
                case 'SPHERE':
                    values[radCode] = seg.radius;
                    values[sagCode] = seg.sagittal;
                    break;

                case 'ASPHERE':
                case 'INVERTED_ASPHERE':
                case 'PROGRESSIVE_ASPHERE':
                    values[radCode] = seg.radius;
                    values[eccCode] = seg.eccentricity;
                    values[sagCode] = seg.sagittal;
                    break;

                case 'LINE':
                    values[sagCode] = seg.sagittal;
                    break;
            }
        }

        proto.setValues(values, {
            form_display:          'HIDDEN',
            form_splitted_display: 'HIDDEN',
        });
        proto.setOldValuesFromValues();
    }
}
